import { Box, Chip, Typography } from "@mui/material";
import isEqualWith from "lodash/isEqualWith";
import React from "react";

export type JsonValueProps = {
  value: unknown;
  comparisonValue?: unknown;
  diffSide?: "old" | "new";
  /** @internal recursion depth */
  depth?: number;
};

const KEY_SX = {
  color: "#999",
  fontSize: 11,
  lineHeight: "16px",
  whiteSpace: "nowrap" as const,
};
const VAL_SX = { fontSize: 12, lineHeight: "16px", fontFamily: "inherit" };
const MAX_DEPTH = 3;

function isPrimitive(v: unknown) {
  return v === null || v === undefined || typeof v !== "object";
}

function parseJson(value: unknown): unknown {
  if (typeof value !== "string") return value;
  try {
    const parsed = JSON.parse(value);
    return parsed !== null && typeof parsed === "object" ? parsed : value;
  } catch {
    return value;
  }
}

function valuesEqual(value: unknown, comparison: unknown) {
  return isEqualWith(value, comparison, (left: unknown, right: unknown) => {
    if (
      typeof left === "number" &&
      typeof right === "string" &&
      right.trim() !== ""
    ) {
      return Number.isFinite(left) && left === Number(right);
    }
    if (
      typeof right === "number" &&
      typeof left === "string" &&
      left.trim() !== ""
    ) {
      return Number.isFinite(right) && right === Number(left);
    }
    return undefined;
  });
}

function matchingItem(value: unknown, index: number, comparison: unknown) {
  if (!Array.isArray(comparison)) return undefined;
  if (value !== null && typeof value === "object" && !Array.isArray(value)) {
    const record = value as Record<string, unknown>;
    const identityKey = ["variant_id", "option_id", "id"].find(
      key => record[key] != null,
    );
    if (identityKey) {
      return comparison.find(
        item =>
          item != null && valuesEqual(item[identityKey], record[identityKey]),
      );
    }
  }
  return comparison[index];
}

function getDiffSx(
  value: unknown,
  comparisonValue: unknown,
  diffSide?: "old" | "new",
) {
  return diffSide && !valuesEqual(value, comparisonValue)
    ? {
        fontWeight: 700,
        color: diffSide === "old" ? "error.main" : "success.main",
      }
    : {};
}

export const JsonValue: React.FC<JsonValueProps> = ({
  value: rawValue,
  comparisonValue: rawComparisonValue,
  diffSide,
  depth = 0,
}) => {
  const value = parseJson(rawValue);
  const comparisonValue = parseJson(rawComparisonValue);
  const diffSx = getDiffSx(value, comparisonValue, diffSide);

  // ── null / undefined / empty ─────────────────────────────────────
  if (value === null || value === undefined || value === "") {
    return (
      <Chip
        variant="outlined"
        size="small"
        label="–"
        sx={{
          height: 16,
          fontSize: 11,
          px: 0.5,
          "& .MuiChip-label": { px: 0.75 },
          ...diffSx,
        }}
      />
    );
  }

  // ── boolean ──────────────────────────────────────────────────────
  if (typeof value === "boolean") {
    return (
      <Chip
        size="small"
        label={value ? "igen" : "nem"}
        color={value ? "success" : "default"}
        sx={{
          height: 16,
          fontSize: 11,
          "& .MuiChip-label": { px: 0.75 },
          ...diffSx,
        }}
      />
    );
  }

  // ── number ───────────────────────────────────────────────────────
  if (typeof value === "number") {
    return (
      <Typography
        component="span"
        variant="caption"
        sx={{ fontFamily: "monospace", ...diffSx }}
      >
        {value}
      </Typography>
    );
  }

  // ── string: try to parse as JSON ─────────────────────────────────
  if (typeof value === "string") {
    return (
      <Typography component="span" variant="caption" sx={diffSx}>
        {value}
      </Typography>
    );
  }

  // ── depth guard: fall back to compact stringify ──────────────────
  if (depth >= MAX_DEPTH) {
    return (
      <Typography
        component="span"
        variant="caption"
        sx={{ fontFamily: "monospace", color: "text.secondary", ...diffSx }}
      >
        {JSON.stringify(value)}
      </Typography>
    );
  }

  // ── array ────────────────────────────────────────────────────────
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return (
        <Typography
          component="span"
          variant="caption"
          color="text.disabled"
          sx={diffSx}
        >
          (üres)
        </Typography>
      );
    }

    // all primitives → inline comma list
    if (value.every(isPrimitive)) {
      return (
        <Typography component="span" variant="caption" sx={diffSx}>
          {value.map(String).join(", ")}
        </Typography>
      );
    }

    // contains objects → numbered rows
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "1px" }}>
        {value.map((item, i) => (
          <Box
            key={i}
            sx={{ display: "flex", gap: 0.5, alignItems: "flex-start" }}
          >
            <Typography
              component="span"
              variant="caption"
              sx={{ ...KEY_SX, minWidth: 16 }}
            >
              {i + 1}.
            </Typography>
            <JsonValue
              value={item}
              comparisonValue={matchingItem(item, i, comparisonValue)}
              diffSide={diffSide}
              depth={depth + 1}
            />
          </Box>
        ))}
      </Box>
    );
  }

  // ── object ───────────────────────────────────────────────────────
  if (typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>).filter(
      ([, v]) => v !== undefined,
    );
    const comparison =
      comparisonValue !== null && typeof comparisonValue === "object"
        ? (comparisonValue as Record<string, unknown>)
        : {};

    if (entries.length === 0) {
      return (
        <Typography
          component="span"
          variant="caption"
          color="text.disabled"
          sx={diffSx}
        >
          (üres)
        </Typography>
      );
    }

    const allLeaf = entries.every(([, v]) => isPrimitive(v));

    if (allLeaf) {
      // inline: key: val · key: val
      return (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            columnGap: "6px",
            rowGap: "1px",
            alignItems: "center",
          }}
        >
          {entries.map(([k, v], i) => (
            <React.Fragment key={k}>
              {i > 0 && (
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ color: "#ccc", userSelect: "none" }}
                >
                  ·
                </Typography>
              )}
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  gap: "3px",
                  alignItems: "center",
                }}
              >
                <Typography component="span" variant="caption" sx={KEY_SX}>
                  {k}:
                </Typography>
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ ...VAL_SX, ...getDiffSx(v, comparison[k], diffSide) }}
                >
                  {v === null || v === undefined ? "–" : String(v)}
                </Typography>
              </Box>
            </React.Fragment>
          ))}
        </Box>
      );
    }

    // stacked: key per row, value may be complex
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
        {entries.map(([k, v]) => (
          <Box
            key={k}
            sx={{ display: "flex", gap: "4px", alignItems: "flex-start" }}
          >
            <Typography
              component="span"
              variant="caption"
              sx={{ ...KEY_SX, pt: "1px" }}
            >
              {k}:
            </Typography>
            <JsonValue
              value={v}
              comparisonValue={comparison[k]}
              diffSide={diffSide}
              depth={depth + 1}
            />
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Typography component="span" variant="caption" sx={diffSx}>
      {String(value)}
    </Typography>
  );
};
